import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

import type { AgentScope } from "../types";
import { AgentAttachmentsAPI } from "./agentAttachmentsApi";
import type { AttachmentFilePart, ComposerAttachment } from "./types";
import {
  attachmentProblem,
  MAX_ATTACHMENTS_PER_MESSAGE,
  toFilePart
} from "./utils";

const errorText = (e: unknown): string => {
  const data = (e as { response?: { data?: { error?: unknown } } })?.response
    ?.data;
  if (data && typeof data.error === "string") return data.error;
  return (e as Error)?.message || "Upload failed";
};

const isAbort = (e: unknown): boolean =>
  (e as { name?: string })?.name === "CanceledError" ||
  (e as { name?: string })?.name === "AbortError";

export type AgentAttachmentsController = {
  enabled: boolean;
  items: ComposerAttachment[];
  /** Upload picked, dropped or pasted files (unreadable ones are refused). */
  add: (files: FileList | File[]) => void;
  /** Drop a file from the composer (deleted on the server if uploaded). */
  remove: (localId: string) => void;
  /** Drop every file from the composer, as `remove` does for one. */
  removeAll: () => void;
  /** Forget everything after the message went out (the files stay). */
  clear: () => void;
  /** The uploaded files as message parts. */
  parts: AttachmentFilePart[];
  uploading: boolean;
};

/**
 * The composer's attachments for `scope`: each file is uploaded as soon as it
 * is picked, so sending only references it. Without a scope the composer
 * offers no attachments.
 */
export const useAgentAttachments = (
  scope: AgentScope | null | undefined
): AgentAttachmentsController => {
  const [items, setItems] = useState<ComposerAttachment[]>([]);
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const aborts = useRef(new Map<string, AbortController>());
  const scopeKind = scope?.kind;
  const scopeId = scope?.id;
  const stableScope = useMemo<AgentScope | null>(
    () => (scopeKind && scopeId ? { kind: scopeKind, id: scopeId } : null),
    [scopeKind, scopeId]
  );

  const patch = useCallback(
    (localId: string, next: Partial<ComposerAttachment>) =>
      setItems((cur) =>
        cur.map((i) => (i.localId === localId ? { ...i, ...next } : i))
      ),
    []
  );

  const add = useCallback(
    (picked: FileList | File[]) => {
      if (!stableScope) return;
      const files = Array.from(picked);
      const room = MAX_ATTACHMENTS_PER_MESSAGE - itemsRef.current.length;
      if (files.length > room) {
        toast.error(
          `Attach up to ${MAX_ATTACHMENTS_PER_MESSAGE} files to one message`
        );
      }
      const accepted: File[] = [];
      for (const file of files.slice(0, Math.max(room, 0))) {
        const problem = attachmentProblem(file);
        if (problem) toast.error(problem);
        else accepted.push(file);
      }
      if (!accepted.length) return;
      const added = accepted.map<[ComposerAttachment, File]>((file) => [
        {
          localId: crypto.randomUUID(),
          filename: file.name,
          contentType: file.type,
          size: file.size,
          status: "uploading",
          progress: 0
        },
        file
      ]);
      setItems((cur) => [...cur, ...added.map(([item]) => item)]);
      for (const [item, file] of added) {
        const abort = new AbortController();
        aborts.current.set(item.localId, abort);
        AgentAttachmentsAPI.upload(stableScope, file, {
          signal: abort.signal,
          onCreated: (attachment) => patch(item.localId, { attachment }),
          onProgress: (f) =>
            patch(item.localId, { progress: Math.round(f * 100) })
        })
          .then((attachment) =>
            patch(item.localId, {
              attachment,
              status: "ready",
              progress: 100
            })
          )
          .catch((e: unknown) => {
            if (isAbort(e)) return;
            const error = errorText(e);
            patch(item.localId, { status: "failed", error });
            toast.error(`Couldn't attach ${file.name}`, { description: error });
          })
          .finally(() => aborts.current.delete(item.localId));
      }
    },
    [patch, stableScope]
  );

  const remove = useCallback(
    (localId: string) => {
      const item = itemsRef.current.find((i) => i.localId === localId);
      if (!item) return;
      aborts.current.get(localId)?.abort();
      aborts.current.delete(localId);
      setItems((cur) => cur.filter((i) => i.localId !== localId));
      if (item.attachment && stableScope)
        void AgentAttachmentsAPI.remove(stableScope, item.attachment.id).catch(
          () => undefined
        );
    },
    [stableScope]
  );

  const removeAll = useCallback(() => {
    const removed = itemsRef.current;
    for (const a of aborts.current.values()) a.abort();
    aborts.current.clear();
    setItems([]);
    if (!stableScope) return;
    for (const item of removed) {
      if (item.attachment)
        void AgentAttachmentsAPI.remove(stableScope, item.attachment.id).catch(
          () => undefined
        );
    }
  }, [stableScope]);

  const clear = useCallback(() => setItems([]), []);

  // A new scope starts with an empty composer; nothing outlives the unmount.
  useEffect(
    () => () => {
      for (const a of aborts.current.values()) a.abort();
      aborts.current.clear();
      setItems([]);
    },
    [stableScope]
  );

  const parts = useMemo(
    () =>
      items.flatMap((i) =>
        i.status === "ready" && i.attachment ? [toFilePart(i.attachment)] : []
      ),
    [items]
  );

  return {
    enabled: stableScope !== null,
    items,
    add,
    remove,
    removeAll,
    clear,
    parts,
    uploading: items.some((i) => i.status === "uploading")
  };
};
